import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import {
  Package,
  TrendingUp,
  DollarSign,
  Activity,
  MapPin,
  BarChart3,
  PieChart
} from 'lucide-react'

interface PackageStatsProps {
  stats: {
    totalPackages: number
    activePackages: number
    inactivePackages: number
    popularPackages: number
    packagesByType: Record<string, number>
    packagesByRegion: Record<string, number>
  }
}

export default function PackageStats({ stats }: PackageStatsProps) {
  const packageTypeColors = {
    BASIC: 'bg-gray-100 text-gray-800',
    STANDARD: 'bg-blue-100 text-blue-800',
    PREMIUM: 'bg-green-100 text-green-800',
    CUSTOM: 'bg-purple-100 text-purple-800'
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
    }).format(value)
  }

  const topRegions = Object.entries(stats.packagesByRegion)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)

  const typePercentage = (type: string) => {
    const total = Object.values(stats.packagesByType).reduce((sum, count) => sum + count, 0)
    return total > 0 ? Math.round((stats.packagesByType[type] / total) * 100) : 0
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Total Packages</p>
                <p className="text-2xl font-bold text-neutral-900">{stats.totalPackages}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.activePackages}</p>
                <p className="text-xs text-neutral-500">
                  {stats.totalPackages > 0 ? Math.round((stats.activePackages / stats.totalPackages) * 100) : 0}% of total
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Activity className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Popular</p>
                <p className="text-2xl font-bold text-amber-600">{stats.popularPackages}</p>
                <p className="text-xs text-neutral-500">
                  {stats.totalPackages > 0 ? Math.round((stats.popularPackages / stats.totalPackages) * 100) : 0}% featured
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Inactive</p>
                <p className="text-2xl font-bold text-red-600">{stats.inactivePackages}</p>
                <p className="text-xs text-neutral-500">
                  {stats.totalPackages > 0 ? Math.round((stats.inactivePackages / stats.totalPackages) * 100) : 0}% disabled
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <div className="h-6 w-6 bg-red-600 rounded-full"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Packages by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <span>Packages by Type</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(stats.packagesByType).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      type === 'BASIC' ? 'bg-gray-500' :
                      type === 'STANDARD' ? 'bg-blue-500' :
                      type === 'PREMIUM' ? 'bg-green-500' :
                      'bg-purple-500'
                    }`}></div>
                    <span className="font-medium">{type}</span>
                    <span className={`px-2 py-1 rounded text-xs ${packageTypeColors[type as keyof typeof packageTypeColors]}`}>
                      {typePercentage(type)}%
                    </span>
                  </div>
                  <span className="font-bold text-lg">{count}</span>
                </div>
              ))}
              {Object.keys(stats.packagesByType).length === 0 && (
                <p className="text-center text-neutral-500 py-4">No packages available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Regions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="h-5 w-5" />
              <span>Top Regions</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topRegions.map(([region, count], index) => (
                <div key={region} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-xs font-bold text-primary-600">
                      {index + 1}
                    </div>
                    <span className="font-medium">{region}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-neutral-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{
                          width: `${topRegions.length > 0 ? (count / topRegions[0][1]) * 100 : 0}%`
                        }}
                      ></div>
                    </div>
                    <span className="font-bold text-sm w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
              {topRegions.length === 0 && (
                <p className="text-center text-neutral-500 py-4">No regions with packages</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <PieChart className="h-5 w-5" />
            <span>Package Insights</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-neutral-50 rounded-lg">
              <div className="text-2xl font-bold text-primary-600 mb-2">
                {stats.totalPackages > 0 ? Math.round((stats.activePackages / stats.totalPackages) * 100) : 0}%
              </div>
              <div className="text-sm text-neutral-600">Package Availability</div>
              <div className="text-xs text-neutral-500 mt-1">
                {stats.activePackages} of {stats.totalPackages} packages are active
              </div>
            </div>

            <div className="text-center p-4 bg-neutral-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-600 mb-2">
                {stats.totalPackages > 0 ? Math.round((stats.popularPackages / stats.totalPackages) * 100) : 0}%
              </div>
              <div className="text-sm text-neutral-600">Featured Packages</div>
              <div className="text-xs text-neutral-500 mt-1">
                {stats.popularPackages} packages marked as popular
              </div>
            </div>

            <div className="text-center p-4 bg-neutral-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 mb-2">
                {Object.keys(stats.packagesByRegion).length}
              </div>
              <div className="text-sm text-neutral-600">Service Regions</div>
              <div className="text-xs text-neutral-500 mt-1">
                Packages available across multiple regions
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}